import { useState } from 'react';

/**
 * useLoadMore 훅의 반환 타입 인터페이스
 * @template T 리스트 아이템의 타입
 */
interface UseLoadMoreReturn<T> {
  /** 현재 화면에 표시되는 아이템 리스트 */
  visibleList: T[];
  /** 더 불러올 데이터가 있는지 여부 */
  hasMore: boolean;
  /** 더 많은 데이터를 불러오는 함수 */
  handleLoadMore: () => void;
  /** 페이지 초기화 함수 */
  resetPage: () => void;
}

/**
 * 페이지네이션 기능을 제공하는 커스텀 훅
 *
 * @template T 리스트 아이템의 타입
 * @param {T[] | undefined} list - 페이지네이션할 전체 리스트
 * @param {number} itemsPerPage - 한 페이지당 표시할 아이템 수
 * @returns {UseLoadMoreReturn<T>} 페이지네이션 관련 상태와 함수
 */
const useLoadMore = <T>(list: T[] | undefined, itemsPerPage: number): UseLoadMoreReturn<T> => {
  const [currentPage, setCurrentPage] = useState(1);

  // 데이터가 없으면 기본값 반환
  if (!list) {
    return {
      visibleList: [],
      hasMore: false,
      handleLoadMore: () => undefined,
      resetPage: () => undefined,
    };
  }

  const itemCount = currentPage * itemsPerPage;
  const visibleList = list.slice(0, itemCount);
  const hasMore = itemCount < list.length;

  /**
   * 다음 페이지 데이터를 불러오는 함수
   */
  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const resetPage = () => {
    setCurrentPage(1);
  };

  return {
    visibleList,
    hasMore,
    handleLoadMore,
    resetPage,
  };
};

export default useLoadMore;
